/* eslint-disable import/prefer-default-export */

/*
 This file is meant for utility methods that need to be shared across screens,
 yet are still too abstract to be a "global util"-

 The idea is to not have too much in here, and refactor any abstract methods
 where possible in the future to be more common.
*/

import { documentAPI } from '@/api';
import { padStart } from '@/utils';

const IMAGE_QUALITY = 0.9;
const IMAGE_MAX_LENGTH = 1024;
const IMAGE_MIN_FILESIZE = 160000;

const calculateProportion = (longSide, shortSide) =>
  Math.floor((IMAGE_MAX_LENGTH / longSide) * shortSide);

const getScaledSize = (width, height) => {
  if (width > IMAGE_MAX_LENGTH && width > height) {
    return {
      width: IMAGE_MAX_LENGTH,
      height: calculateProportion(width, height),
    };
  }

  if (height > IMAGE_MAX_LENGTH && height > width) {
    return {
      width: calculateProportion(height, width),
      height: IMAGE_MAX_LENGTH,
    };
  }

  return {
    width,
    height,
  };
};

const resizeImage = file => new Promise((resolve) => {
  const img = new Image();

  img.onload = () => {
    const { width, height } = getScaledSize(img.width, img.height);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    ctx.canvas.toBlob((blob) => {
      const newFile = new File([blob], file.name, {
        type: file.type,
      });

      if (newFile.size >= file.size) {
        resolve(file);
      }

      resolve(newFile);
    }, file.type, IMAGE_QUALITY);
  };

  img.src = URL.createObjectURL(file);
});

const getPromiseProgress = (promises, progressCallback) => {
  let d = 0;
  progressCallback({
    current: d,
    total: promises.length,
  });

  for (const p of promises) { // eslint-disable-line no-restricted-syntax
    p.then(() => { // eslint-disable-line no-loop-func
      d++; // eslint-disable-line no-plusplus
      progressCallback({
        current: d,
        total: promises.length,
      });
    });
  }

  return Promise.all(promises);
};

const getFile = (file) => {
  const isImage = file.type.includes('image/');

  if (isImage && file.size >= IMAGE_MIN_FILESIZE) {
    return resizeImage(file);
  }

  return Promise.resolve(file);
};

export const uploadChargeDocuments = async ({
  files = [], title, category, chargeId, countProgressCallback, sizeProgressCallback,
}) => {
  const multipleFiles = files.length > 1;
  const filesProgress = {};

  try {
    /* eslint-disable-next-line arrow-parens */
    const fileUploads = files.map(async (file, i) => {
      let uploadTitle;

      if (!multipleFiles) {
        uploadTitle = title;
      } else {
        const index = i + 1;
        uploadTitle = title ? `${title}${padStart(index, 2, '0')}` : file.name;
      }

      const fileBase = {
        titulo: uploadTitle,
        categoria: category,
        encargo_id: chargeId,
      };

      const fileToUpload = await getFile(file);

      return documentAPI.create({
        ...fileBase,
        file: fileToUpload,
      }, (p) => {
        filesProgress[i] = p;
        sizeProgressCallback(filesProgress);
      });
    });

    const response = await getPromiseProgress(fileUploads, countProgressCallback);

    const notificationString = `Documento${multipleFiles ? 's' : ''} subido${
      multipleFiles ? 's' : ''
    }`;

    return {
      response,
      notificationPayload: {
        type: 'success',
        title: notificationString,
        message: `${notificationString} con éxito.`,
      },
    };
  } catch (err) {
    return Promise.reject(err);
  }
};
